/**
 * 工作台「页面」内容树：公网真实 path（静态页 + 栏目文件夹 + 真条目）。
 * 与设计地址栏版式树（`*` / `:slug`）分开，勿混用。
 */

export type ContentTreePage = {
  id: number;
  slug: string;
  title: string;
  is_home: boolean;
};

export type ContentTreeCollection = {
  id: number;
  key: string;
  nav_label: string;
};

export type ContentTreeItem = {
  id: number;
  slug: string;
  settings: Record<string, unknown>;
  url: string;
};

export type ContentTreeNodeKind = "static" | "collection_folder" | "collection_item";

export type ContentTreeNode = {
  key: string;
  kind: ContentTreeNodeKind;
  title: string;
  path: string;
  slug: string | null;
  pageId: number | null;
  collectionId: number | null;
  itemId: number | null;
  isHome: boolean;
  /** 栏目文件夹且尚无条目 */
  empty?: boolean;
  children: ContentTreeNode[];
};

function normalizeSlug(slug: string): string {
  return slug.trim().replace(/^\/+|\/+$/g, "");
}

function isPartialSlug(slug: string): boolean {
  return normalizeSlug(slug).startsWith("_");
}

function isHomePage(page: ContentTreePage, homeSlug: string): boolean {
  return page.is_home || normalizeSlug(page.slug) === normalizeSlug(homeSlug);
}

function pathForSlug(slug: string, homeSlug: string): string {
  if (!slug || slug === homeSlug || slug === "index") return "/";
  return `/${slug}`;
}

function itemTitle(item: ContentTreeItem): string {
  const settings = item.settings ?? {};
  const raw = settings.title ?? settings.name;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return item.slug;
}

function compareNodes(a: ContentTreeNode, b: ContentTreeNode): number {
  if (a.isHome && !b.isHome) return -1;
  if (!a.isHome && b.isHome) return 1;
  if (a.kind === "collection_folder" && b.kind !== "collection_folder") return 1;
  if (a.kind !== "collection_folder" && b.kind === "collection_folder") return -1;
  return a.title.localeCompare(b.title, "zh");
}

/**
 * 聚合静态页 + 栏目 + 条目为可折叠内容树。空栏目仍保留文件夹节点。
 */
export function buildContentTree(
  pages: ContentTreePage[],
  homeSlug: string,
  collections: ContentTreeCollection[],
  itemsByCollectionId: Record<number, ContentTreeItem[]>,
): ContentTreeNode {
  const root: ContentTreeNode = {
    key: "dir:/",
    kind: "static",
    title: "/",
    path: "/",
    slug: homeSlug,
    pageId: null,
    collectionId: null,
    itemId: null,
    isHome: true,
    children: [],
  };

  const collectionKeys = new Set(collections.map((c) => c.key));

  for (const page of pages.filter((p) => !isPartialSlug(p.slug))) {
    const slug = normalizeSlug(page.slug);
    // 栏目 key 同名的静态页由栏目文件夹接管，避免重复
    if (collectionKeys.has(slug)) continue;

    const title = page.title.trim() || slug || "未命名";
    const path = pathForSlug(page.slug, homeSlug);
    root.children.push({
      key: `page:${page.id}`,
      kind: "static",
      title,
      path,
      slug: page.slug,
      pageId: page.id,
      collectionId: null,
      itemId: null,
      isHome: isHomePage(page, homeSlug),
      children: [],
    });
  }

  for (const collection of collections) {
    const items = itemsByCollectionId[collection.id] ?? [];
    const folderPath = `/${collection.key}`;
    const children: ContentTreeNode[] = items.map((item) => ({
      key: `item:${collection.id}:${item.id}`,
      kind: "collection_item" as const,
      title: itemTitle(item),
      path: item.url || `/${collection.key}/${item.slug}`,
      slug: item.slug,
      pageId: null,
      collectionId: collection.id,
      itemId: item.id,
      isHome: false,
      children: [],
    }));
    children.sort((a, b) => (a.slug ?? "").localeCompare(b.slug ?? "", "en"));

    root.children.push({
      key: `collection:${collection.id}`,
      kind: "collection_folder",
      title: collection.nav_label,
      path: folderPath,
      slug: collection.key,
      pageId: null,
      collectionId: collection.id,
      itemId: null,
      isHome: false,
      empty: children.length === 0,
      children,
    });
  }

  root.children.sort(compareNodes);
  return root;
}

function nodeMatches(node: ContentTreeNode, query: string): boolean {
  return (
    node.title.toLowerCase().includes(query) ||
    node.path.toLowerCase().includes(query) ||
    (node.slug?.toLowerCase().includes(query) ?? false)
  );
}

function filterNode(node: ContentTreeNode, query: string): ContentTreeNode | null {
  if (nodeMatches(node, query)) return node;
  const children = node.children
    .map((child) => filterNode(child, query))
    .filter((child): child is ContentTreeNode => child != null);
  if (children.length === 0) return null;
  return { ...node, children };
}

/** 按标题 / path / slug 过滤；命中子节点时保留祖先。 */
export function filterContentTree(root: ContentTreeNode, rawQuery: string): ContentTreeNode {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return root;
  const children = root.children
    .map((child) => filterNode(child, query))
    .filter((child): child is ContentTreeNode => child != null);
  return { ...root, children };
}
